import { Icon, IconProps, NumericBadge } from '@platform/components';
import cx from 'classix';
import Skeleton from 'react-loading-skeleton';
import { useLocation } from 'wouter';
import EVENTS from '../analytics/events';
import dataTestids from '../constants/test-ids';
import FlexibleLink from '../util/FlexibleLink';
import useNavItems, { NavItemConfig } from './navigation/useNavItems';

/** Only shown in the mobile drawer — desktop surfaces this via `AppHeader`'s `NavUserMenu`
 * avatar dropdown, which is hidden on mobile per the latest Figma. */
const logoutItem: NavItemConfig = {
  id: 'logout',
  title: 'Log out',
  href: import.meta.env.VITE_API_HOST + '/login/logout',
  iconType: 'log-out-01',
  linkType: 'external',
  eventName: EVENTS.DASHBOARD.PROFILE_DROPDOWN.LOGOUT_CLICKED,
};

const NavItem = ({
  title,
  href,
  iconType,
  viewbox,
  linkType,
  eventName,
  newTab,
  badge,
  isActive,
  externalIndicator,
}: {
  title: string;
  href: string;
  iconType: IconProps['type'];
  viewbox?: string;
  eventName: string;
  linkType?: Parameters<typeof FlexibleLink>[0]['type'];
  newTab?: boolean;
  badge?: number;
  isActive: boolean;
  externalIndicator?: boolean;
}) => {
  return (
    <FlexibleLink href={href} type={linkType ?? 'internal'} eventName={eventName} newTab={newTab}>
      <div
        className={cx(
          'flex items-center gap-2 px-3 py-2 rounded-[6px] group cursor-pointer',
          isActive ? 'bg-secondary-brand-25' : 'hover:bg-gray-50',
        )}
      >
        <Icon type={iconType} viewbox={viewbox} size='20' stroke='gray-500' />
        <span
          className={cx(
            'flex-1 text-base text-gray-700',
            isActive ? 'font-semibold' : 'font-medium',
          )}
        >
          {title}
        </span>
        {badge !== undefined && <NumericBadge color='orange' size='sm' number={badge} />}
        {externalIndicator && <Icon type='arrow-narrow-up-right' size='16' stroke='gray-500' />}
      </div>
    </FlexibleLink>
  );
};

const AskAQuestionCard = ({
  title,
  href,
  linkType,
  newTab,
  eventName,
  iconType,
}: NavItemConfig) => (
  <FlexibleLink href={href} type={linkType ?? 'external'} eventName={eventName} newTab={newTab}>
    <div className='flex items-center gap-3 p-4 rounded-xl border border-gray-100 shadow-xs bg-white group cursor-pointer'>
      <span className='flex items-center justify-center shrink-0 size-8 rounded-full bg-secondary-brand-25 border border-gray-100'>
        <Icon type={iconType} size='16' stroke='gray-500' />
      </span>
      <span className='text-sm font-semibold text-gray-700'>{title}</span>
    </div>
  </FlexibleLink>
);

// `~` is wouter's absolute-escape prefix (see useNavItems.ts) — strip it to compare against the
// browser's actual pathname.
const toAbsolutePathname = (href: string) => (href.startsWith('~') ? href.slice(1) : href);

const renderItem = (item: NavItemConfig, currentPathname: string, onItemClick?: () => void) => {
  const content =
    item.id === 'ask-a-question' ?
      <AskAQuestionCard {...item} />
    : <NavItem
        title={item.title}
        href={item.href}
        iconType={item.iconType}
        viewbox={item.viewbox}
        linkType={item.linkType}
        newTab={item.newTab}
        eventName={item.eventName}
        badge={item.badge}
        isActive={item.linkType === 'internal' && currentPathname === toAbsolutePathname(item.href)}
        externalIndicator={item.externalIndicator}
      />;

  return (
    <div key={item.id} onClick={onItemClick}>
      {content}
    </div>
  );
};

/** The left sidebar nav list. Used both as the fixed-width 280px column alongside dashboard
 * content on desktop, and full-width inside the mobile nav drawer (via `variant='mobile'`). */
const Sidebar = ({
  variant = 'desktop',
  onItemClick,
}: {
  variant?: 'desktop' | 'mobile';
  onItemClick?: () => void;
}) => {
  const { isLoading, mainItems, footerItems } = useNavItems();
  //Subscribed to changes even though the pathname can't be used directly for comparison
  useLocation();
  const currentPathname = typeof window === 'undefined' ? '' : window.location.pathname;

  const displayedFooterItems =
    variant === 'mobile' ?
      footerItems.flatMap((item) => (item.id === 'ask-a-question' ? [logoutItem, item] : [item]))
    : footerItems;

  return (
    <div
      className={cx(
        'bg-white p-4 flex flex-col',
        variant === 'desktop' &&
          'sticky top-0 h-[calc(100vh-var(--navbar-height))] w-[280px] shrink-0 overflow-y-auto border-r border-gray-100',
        variant === 'mobile' && 'flex-1',
      )}
      role='navigation'
      data-testid={dataTestids.DASHBOARD.LEFTNAV}
    >
      {isLoading ?
        <div className='flex flex-col gap-4'>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={`skeleton-${i}`} count={1} />
          ))}
        </div>
      : <>
          <div className='flex flex-col gap-1'>
            {mainItems.map((item) => renderItem(item, currentPathname, onItemClick))}
          </div>
          <div className='flex-1 max-h-[404px]' aria-hidden='true' />
          <div className='flex flex-col gap-4'>
            {displayedFooterItems.flatMap((item) => {
              const node = renderItem(item, currentPathname, onItemClick);
              return variant === 'mobile' && item.id === 'account-settings' ?
                  [node, <hr key={`${item.id}-divider`} className='border-gray-100' />]
                : [node];
            })}
          </div>
        </>
      }
    </div>
  );
};

export default Sidebar;
